import { useState, useEffect } from 'react';
import { Users, Target, Sparkles } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * UserCounter Component
 * Displays the current registered user count and points redemption progress
 * 
 * @param {Object} props
 * @param {string} props.variant - 'banner' | 'compact' | 'progress'
 * @param {string} props.className - Additional CSS classes
 */
export const UserCounter = ({ variant = 'compact', className = '' }) => {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await fetch(`${API}/stats/public`);
        if (response.ok) {
          const data = await response.json();
          setStats(data);
        }
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
    
    // Refresh every 5 minutes
    const interval = setInterval(fetchStats, 300000);
    return () => clearInterval(interval);
  }, []);

  if (loading || !stats) {
    return null;
  }

  const { total_users, points_redemption } = stats;
  const { threshold, is_active, users_until_active, progress_percent } = points_redemption || {};

  // Compact variant - just the user count
  if (variant === 'compact') {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <Users className="w-4 h-4 text-emerald-600" />
        <span className="text-sm font-medium">
          {total_users?.toLocaleString()} {t('user_counter.users_registered')}
        </span>
      </div>
    );
  }

  // Progress variant - shows progress to 50k threshold
  if (variant === 'progress') {
    return (
      <motion.div 
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className={`bg-gradient-to-r from-emerald-50 to-amber-50 dark:from-emerald-900/20 dark:to-amber-900/20 rounded-2xl p-4 ${className}`}
      >
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 bg-emerald-100 dark:bg-emerald-800 rounded-xl flex items-center justify-center">
            <Target className="w-5 h-5 text-emerald-600" />
          </div>
          <div>
            <h4 className="font-semibold text-gray-900 dark:text-white">
              {t('user_counter.redemption_title')}
            </h4>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {is_active 
                ? t('user_counter.redemption_active')
                : t('user_counter.redemption_progress', { users: users_until_active?.toLocaleString() })
              }
            </p>
          </div>
        </div>
        
        {/* Progress bar */}
        <div className="relative h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
          <motion.div 
            initial={{ width: 0 }}
            animate={{ width: `${Math.min(100, progress_percent)}%` }}
            transition={{ duration: 1, ease: "easeOut" }}
            className={`absolute h-full rounded-full ${
              is_active 
                ? 'bg-gradient-to-r from-emerald-500 to-emerald-400' 
                : 'bg-gradient-to-r from-amber-500 to-emerald-500'
            }`}
          />
        </div>
        
        <div className="flex justify-between mt-2 text-xs text-gray-500 dark:text-gray-400">
          <span>{total_users?.toLocaleString()} {t('user_counter.users')}</span>
          <span>{threshold?.toLocaleString()} {t('user_counter.goal')}</span>
        </div>
      </motion.div>
    );
  }

  // Banner variant - prominent banner for landing pages
  if (variant === 'banner') {
    return (
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className={`relative overflow-hidden rounded-2xl ${className}`}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-amber-400 via-amber-500 to-emerald-500" />
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzRjMC0yLjIgMS44LTQgNC00czQgMS44IDQgNC0xLjggNC00IDQtNC0xLjgtNC00eiIvPjwvZz48L2c+PC9zdmc+')] opacity-30" />
        
        <div className="relative z-10 p-6 text-center text-white">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Sparkles className="w-5 h-5" />
            <span className="text-sm font-medium uppercase tracking-wide">
              {t('user_counter.milestone')}
            </span>
          </div>
          
          <h3 className="text-2xl sm:text-3xl font-bold mb-2">
            {is_active 
              ? t('user_counter.redemption_unlocked')
              : t('user_counter.redemption_unlock_at', { threshold: threshold?.toLocaleString() })
            }
          </h3>
          
          <p className="text-sm opacity-90 mb-3">
            {is_active
              ? t('user_counter.can_redeem_now')
              : t('user_counter.need_more_users', { count: users_until_active?.toLocaleString() })
            }
          </p>

          {/* Hotel Rewards Highlight */}
          <div className="bg-white/20 backdrop-blur-sm rounded-xl p-3 mb-4 max-w-lg mx-auto">
            <p className="text-sm font-semibold mb-1">{t('user_counter.hotel_rewards_title')}</p>
            <p className="text-xs opacity-90">{t('user_counter.hotel_rewards_desc')}</p>
          </div>
          
          {/* Progress */}
          <div className="max-w-md mx-auto">
            <div className="relative h-4 bg-white/20 rounded-full overflow-hidden">
              <motion.div 
                initial={{ width: 0 }}
                animate={{ width: `${Math.min(100, progress_percent)}%` }}
                transition={{ duration: 1.5, ease: "easeOut" }}
                className="absolute h-full bg-white rounded-full"
              />
            </div>
            <div className="flex justify-between mt-2 text-xs opacity-80">
              <span>{total_users?.toLocaleString()} {t('user_counter.registered')}</span>
              <span>{progress_percent}%</span>
            </div>
          </div>
        </div>
      </motion.div>
    );
  }

  return null;
};

export default UserCounter;
